import { useState, useRef, useCallback, useMemo, useLayoutEffect, useEffect } from 'react'
import useGraphCandleWindow from './useGraphCandleWindow'
import useBacktestProgress from './useBacktestProgress'
import useEquityCurve from './useEquityCurve'
import usePlayback from './usePlayback'
import { getCandleCountForTimeframe, formatDateLabel } from '../../utils/chartUtils'
import { parseProgressTimestamp } from '../utils/timeParsing'

const USER_CHART_NAVIGATION_WINDOW_MS = 500

export const AVAILABLE_SPEEDS: readonly number[] = [0.5, 1, 2, 5, 10, 25, 50]

interface UseBacktestChartStateParams {
  btid: string
  symbol: string
  initialTimeframe: string
  initialStatus: number
  initialProgressBacktest: string | null
  initialProgressView: string | null
  rangeStartTs: number
  rangeEndTs: number
}

export default function useBacktestChartState({
  btid,
  symbol,
  initialTimeframe,
  initialStatus,
  initialProgressBacktest,
  initialProgressView,
  rangeStartTs,
  rangeEndTs,
}: UseBacktestChartStateParams) {
  const rangeTotal = rangeEndTs - rangeStartTs
  const [backtestStatus, setBacktestStatus] = useState(initialStatus)
  const [timeframe, setTimeframe] = useState<string>(initialTimeframe)
  const [speed, setSpeed] = useState<number>(10)
  // Queued auto-play request. Set when the user presses Reset ("Restart
  // replay"); consumed by the autoplay-fire effect below once the candle
  // window is loaded (isLoading=false) and the play button would actually
  // do something (not at the buffered boundary).
  const [autoplayPending, setAutoplayPending] = useState(false)

  const initialProgressViewTs = useMemo(() => {
    const saved = parseProgressTimestamp(initialProgressView)
    if (saved !== null) return saved
    // Completed backtest that was never replayed: land on the fully-drawn
    // end state instead of an empty chart at the range start.
    const isInitiallyCompleted = initialStatus !== 0 && initialStatus !== 1
    return isInitiallyCompleted ? rangeEndTs : rangeStartTs
  }, [initialProgressView, initialStatus, rangeStartTs, rangeEndTs])
  const initialProgressBacktestTs = useMemo(
    () => parseProgressTimestamp(initialProgressBacktest),
    [initialProgressBacktest],
  )

  const [progressViewTs, setProgressViewTs] = useState(initialProgressViewTs)
  const progressViewTsRef = useRef(progressViewTs)
  useLayoutEffect(() => { progressViewTsRef.current = progressViewTs }, [progressViewTs])
  const programmaticRangeRef = useRef<{ from: number; to: number } | null>(null)
  const rangeChangeSuppressedRef = useRef(false)
  const lastUserChartNavigationAtRef = useRef(0)

  const handleSliderJump = useCallback((targetTs: number) => {
    const currentProgress = progressViewTsRef.current
    if (targetTs > currentProgress) {
      setProgressViewTs(targetTs)
    }
  }, [])

  const {
    candles,
    isLoading,
    orders,
    effectiveCandleCount,
    chartRef,
    handleVisibleRangeChange: gwHandleVisibleRangeChange,
    maxAvailableTs,
    isInProgress,
    progressPercent,
    maxSliderFraction,
    updateBacktestProgress,
    sliderPosition,
    sliderBarRef,
    handleSliderMouseDown: gwHandleSliderMouseDown,
    windowIndicator,
    sliderDateLabel,
    dateRangeLabel,
    loadWindow,
    extendWindow,
    applyVisibleRange,
    maxAvailableTsRef,
  } = useGraphCandleWindow({
    symbol,
    timeframe,
    rangeStartTs,
    rangeEndTs,
    btid,
    programmaticRangeRef,
    rangeChangeSuppressedRef,
    initialMaxAvailableTs: initialProgressBacktestTs,
    displayBoundaryTs: progressViewTs,
    initialCenterTs: initialProgressViewTs > rangeStartTs ? initialProgressViewTs : null,
    onSliderJump: handleSliderJump,
  })

  useBacktestProgress({
    btid,
    backtestStatus,
    initialProgressBacktest,
    rangeStartTs,
    rangeEndTs,
    updateBacktestProgress,
    onStatusChange: setBacktestStatus,
  })

  const isBacktestCompleted = backtestStatus !== 0 && backtestStatus !== 1

  const { points: equityPoints, runningTotals: equityRunningTotals } = useEquityCurve({
    btid,
    maxAvailableTs: maxAvailableTs ?? null,
  })

  const candlesRef = useRef(candles)
  useLayoutEffect(() => { candlesRef.current = candles }, [candles])

  const playback = usePlayback({
    btid,
    rangeStartTs,
    rangeEndTs,
    progressViewTs,
    onProgressViewChange: setProgressViewTs,
    timeframe,
    speed,
    candlesRef,
    maxAvailableTsRef,
    extendWindow,
    loadWindow,
    applyVisibleRange,
  })

  const { isPlaying, playStartedAtMs, playElapsedSec } = playback

  const displayedCandles = useMemo(() => {
    if (candles.length === 0 || effectiveCandleCount === candles.length) return candles
    return candles.slice(0, effectiveCandleCount)
  }, [candles, effectiveCandleCount])

  const displayedOrders = useMemo(() => {
    const boundary = maxAvailableTs !== null
      ? Math.min(progressViewTs, maxAvailableTs)
      : progressViewTs
    if (orders.length === 0) return orders
    return orders.filter(o => o.time <= boundary)
  }, [orders, progressViewTs, maxAvailableTs])

  const equityDisplayBoundaryTs = useMemo(
    () => maxAvailableTs !== null ? Math.min(progressViewTs, maxAvailableTs) : progressViewTs,
    [progressViewTs, maxAvailableTs],
  )

  const currentTimestamp = useMemo(
    () => Math.floor(rangeStartTs + sliderPosition * rangeTotal),
    [rangeStartTs, sliderPosition, rangeTotal],
  )

  const progressViewPosition = useMemo(
    () => Math.max(0, Math.min(maxSliderFraction, (progressViewTs - rangeStartTs) / rangeTotal)),
    [maxSliderFraction, progressViewTs, rangeStartTs, rangeTotal],
  )

  const progressViewDateLabel = useMemo(
    () => formatDateLabel(progressViewTs),
    [progressViewTs],
  )

  const visibleCount = useMemo(
    () => getCandleCountForTimeframe(timeframe, displayedCandles.length),
    [timeframe, displayedCandles.length],
  )

  const isPlayingRef = useRef(false)
  useLayoutEffect(() => { isPlayingRef.current = isPlaying }, [isPlaying])

  const handleChartUserNavigate = useCallback(() => {
    // User panned/dragged the chart — cancel any queued auto-play so we don't
    // surprise them by starting playback after they actively navigated away.
    setAutoplayPending(false)
    lastUserChartNavigationAtRef.current = Date.now()
    if (isPlayingRef.current) {
      playback.pause()
    }
  }, [playback])

  const handleVisibleRangeChange = useCallback((range: { from: number; to: number }) => {
    const now = Date.now()
    const isSuppressed = rangeChangeSuppressedRef.current
    const expectedRange = programmaticRangeRef.current
    const isRecentUserNavigation =
      now - lastUserChartNavigationAtRef.current < USER_CHART_NAVIGATION_WINDOW_MS
    if (isSuppressed) return

    if (expectedRange) {
      const matchesProgrammaticRange =
        Math.abs(range.from - expectedRange.from) < 1 &&
        Math.abs(range.to - expectedRange.to) < 1
      if (matchesProgrammaticRange) {
        programmaticRangeRef.current = null
        return
      }
    }

    if (isPlayingRef.current && isRecentUserNavigation) {
      lastUserChartNavigationAtRef.current = 0
      playback.pause()
    }
    gwHandleVisibleRangeChange(range)
  }, [gwHandleVisibleRangeChange, playback])

  const handleSliderMouseDown = useCallback((e: React.MouseEvent) => {
    if (isPlayingRef.current) {
      playback.pause()
    }
    gwHandleSliderMouseDown(e)
  }, [gwHandleSliderMouseDown, playback])

  const handlePlay = useCallback(() => {
    // lastUserChartNavigationAtRef.current = 0
    playback.play()
  }, [playback])

  const handlePause = useCallback(() => {
    // Explicit user pause — drop any queued auto-play.
    setAutoplayPending(false)
    playback.pause()
  }, [playback])

  // Disabled when the replay is at the buffered boundary (either the end of
  // the configured range or the latest candle the backtest has produced).
  // Without the maxAvailableTs cap, pressing play while the backtest is still
  // catching up would just spin in the play-loop's wait-for-data branch.
  const playBoundaryTs = maxAvailableTs ?? rangeEndTs
  const isPlayDisabled = progressViewTs >= playBoundaryTs

  // ---- Auto-play orchestration ----
  // Hold play()/jumpTo() in refs so the effects below don't need playback in
  // their deps. Both are technically stable via useCallback, but routing
  // through refs insulates this from future signature changes.
  const playRef = useRef(playback.play)
  useLayoutEffect(() => { playRef.current = playback.play }, [playback.play])
  const jumpToRef = useRef(playback.jumpTo)
  useLayoutEffect(() => { jumpToRef.current = playback.jumpTo }, [playback.jumpTo])

  // Detect the "backtest just became ready" transition (PENDING/RUNNING -> any
  // terminal status). Failed/cancelled statuses (200/201) never reach this
  // hook — BotBacktest.tsx renders a different UI for them — so any
  // active->terminal transition here means a successful completion.
  const prevStatusRef = useRef(initialStatus)
  useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = backtestStatus
    const wasActive = prev === 0 || prev === 1
    const isActiveNow = backtestStatus === 0 || backtestStatus === 1
    if (wasActive && !isActiveNow) {
      // Backtest just completed: land on the fully-drawn chart at the end.
      // Playing is a deliberate act from here (the "Restart replay" button).
      // If the user is actively replaying mid-run, don't yank them — the play
      // loop naturally runs to the end and stops there anyway.
      if (isPlayingRef.current) return
      jumpToRef.current(rangeEndTs)
    }
  }, [backtestStatus, rangeEndTs])

  // Fire the queued auto-play once it's safe to do so. The effect runs after
  // React commits, so isLoading=false guarantees the candle window for
  // progressViewTs is already in candlesRef — play() will skip its
  // isCurrentWindowLoaded fallback and NOT trigger a duplicate loadWindow.
  useEffect(() => {
    if (!autoplayPending) return
    if (isLoading) return
    if (isPlayDisabled) {
      // Nothing to play (already at the buffered boundary). Drop the request
      // so we don't surprise the user later when isLoading flips again.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot signal: clearing the flag IS the effect's purpose
      setAutoplayPending(false)
      return
    }
    // Consume the queued auto-play. The follow-up effect run early-returns at
    // `if (!autoplayPending) return`, so we don't get a feedback loop.
    setAutoplayPending(false)
    playRef.current()
  }, [autoplayPending, isLoading, isPlayDisabled])

  const handleSpeedChange = useCallback((nextSpeed: number) => { setSpeed(nextSpeed) }, [])

  const handleCycleSpeed = useCallback(() => {
    setSpeed(prev => {
      const list = AVAILABLE_SPEEDS
      const idx = list.indexOf(prev)
      const nextIdx = idx === -1 ? 0 : (idx + 1) % list.length
      return list[nextIdx]
    })
  }, [])

  const handleReset = useCallback(() => {
    // Reset to rangeStart and queue an auto-play. The fire effect waits for
    // reset's own loadWindow to finish (isLoading flips back to false) before
    // calling play(), which avoids the duplicate-loadWindow race the original
    // implementation had.
    playback.reset()
    setAutoplayPending(true)
  }, [playback])

  const handleTimeframeChange = useCallback((tf: string) => {
    playback.pause()
    setTimeframe(tf)
  }, [playback])

  const handleEquityTimeClick = useCallback((timestamp: number) => {
    // Scrubbing is an explicit "I want to look here, paused" intent — drop
    // any queued auto-play so a recently-queued one doesn't fire mid-scrub.
    setAutoplayPending(false)
    playback.jumpTo(timestamp)
  }, [playback])

  return {
    timeframe,
    isPlaying,
    speed,
    backtestStatus,
    isBacktestCompleted,
    candles: displayedCandles,
    orders: displayedOrders,
    isLoading,
    chartRef,
    maxAvailableTs,
    isInProgress,
    progressPercent,
    maxSliderFraction,
    progressViewPosition,
    progressViewDateLabel,
    equityDisplayBoundaryTs,
    sliderPosition,
    sliderBarRef,
    windowIndicator,
    sliderDateLabel,
    dateRangeLabel,
    visibleCount,
    currentTimestamp,
    equityPoints,
    equityRunningTotals,
    isPlayDisabled,
    handleVisibleRangeChange,
    handleChartUserNavigate,
    handleSliderMouseDown,
    handlePlay,
    handlePause,
    handleReset,
    handleSpeedChange,
    handleCycleSpeed,
    handleTimeframeChange,
    handleEquityTimeClick,
    playStartedAtMs,
    playElapsedSec,
    progressViewTs,
  }
}
